// Tiny wrapper around @dnd-kit/sortable. Exposes a single <SortableList>
// component that takes an array of items + a render function. Handles mouse
// AND touch input.
//
// Mobile UX note: a scene/slide card can fill the entire viewport on a phone
// — if we set `touchAction: "none"` here, the browser can't scroll the page
// while the finger is on the card, so the user gets stuck. Instead we keep
// the default touch action AND require a longer press (500ms) before drag
// activates: a normal scroll swipe goes straight through; only an
// intentional long-press starts a drag.
import React from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export const SortableItem = ({ id, children, className = "", testId }) => {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 50 : "auto",
    // While actively dragging we lock touchAction so the gesture stays a
    // drag (no fight with the browser). When idle we leave the default
    // touchAction so vertical scrolling works normally on mobile.
    touchAction: isDragging ? "none" : "auto",
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={className}
      data-testid={testId}
    >
      {children}
    </div>
  );
};

export const SortableList = ({ items, strategy, onReorder, children }) => {
  // PointerSensor (desktop): tiny distance threshold so clicks on inner
  // buttons stay clicks, not micro-drags.
  // TouchSensor (mobile): 500ms long-press required. A normal scroll swipe
  // takes ~50-150ms and won't pass this gate — only intentional press will.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 500, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.indexOf(active.id);
    const newIndex = items.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(items, oldIndex, newIndex);
    onReorder?.(reordered, { oldIndex, newIndex });
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items} strategy={strategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
};

export default SortableList;
